import { useCallback, useContext, useSyncExternalStore } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import type { BootstrapResponse } from "@townsy/contracts";
import { queryKeys } from "../api/queries";
import { useTownStore } from "../state/store";

const noSubscription = () => () => {};

/**
 * The bootstrap: the town's copy (set by the town layout), else the bootstrap query's cached copy, so a page opened
 * directly (e.g. `/directory`, which renders the shop and real-estate views outside the town) reads the same values.
 * Works without a query client (server-rendered unit tests): the town's copy only.
 */
function useBootstrapData(): BootstrapResponse | null {
  const inTown = useTownStore((s) => s.bootstrap);
  const client = useContext(QueryClientContext);
  const subscribe = useCallback((onChange: () => void) => (client ? client.getQueryCache().subscribe(onChange) : noSubscription()), [client]);
  const read = () => client?.getQueryData<BootstrapResponse>(queryKeys.bootstrap) ?? null;
  const cached = useSyncExternalStore(subscribe, read, read);
  return inTown ?? cached;
}

/**
 * Founder 2026-10-27: the launch prices are approved for production ("a shop starting price is $5"). Outside production
 * (development, staging, tests) the price notes keep saying they are development test prices.
 */
export function useTestPrices(): boolean {
  return useBootstrapData()?.environment !== "production";
}

/**
 * ADR-0046: the server takes real card payments (Stripe live mode). Price notes say "Stripe test mode" only when this is
 * false (`cardPhrase`); a live buyer never reads test wording.
 */
export function useLivePayments(): boolean {
  return useBootstrapData()?.features.paymentsMode === "stripe_live";
}
